'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Users, Flame, Zap, Send, X, CheckCircle, ChevronDown, ChevronUp, Bell, Sparkles, TrendingUp, Star, Radio, BookOpen, Hash, Paperclip, Award, Target, Clock, ThumbsUp, Eye, Quote, Shield, PlusCircle, LogIn, Image, Link2, Video } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import {
  getEras, getForumPosts, createForumPost, getForumReplies, createForumReply,
  getHistorianAmas, getAmaQuestions, submitAmaQuestion,
  type Era, type ForumPost, type ForumReply, type HistorianAma, type AmaQuestion,
} from '@/lib/db/community-spaces';
import ReactionButtons from '@/components/ReactionButtons';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';


// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_POSTS: ForumPost[] = [
  {
    id: 'mock-1',
    era_id: 'era-1',
    author_id: 'user-1',
    author_name: 'Dr. Amara Osei',
    author_avatar: null,
    author_tier: 'historian',
    title: 'The Oral Tradition of the Ashanti Kingdom — What Written Records Miss',
    body: 'For decades, colonial-era historians dismissed Ashanti oral traditions as "unreliable" compared to written European accounts. But recent linguistic analysis of Asante Twi proverbs embedded in griotic recitations reveals a remarkably consistent chronology of the Osei Tutu dynasty — one that aligns with Dutch East India Company trade ledgers from 1701. The oral record was not imprecise; it was encoded differently. I\'d love to hear from anyone who has worked with Akan oral sources directly.',
    reply_count: 14,
    upvote_count: 0,
    is_pinned: true,
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-2',
    era_id: 'era-2',
    author_id: 'user-2',
    author_name: 'Kofi Mensah',
    author_avatar: null,
    author_tier: 'elder',
    title: 'Pre-Colonial Trade Routes: The Saharan Gold-Salt Exchange Revisited',
    body: 'The conventional narrative places the Ghana Empire\'s wealth solely in gold. But my grandmother\'s family oral history — corroborated by Ibn Battuta\'s 1352 account — describes a sophisticated credit system using kola nuts as promissory tokens across the Saharan routes. This predates European banking instruments by at least two centuries. Has anyone found similar references in Malian or Songhai oral archives?',
    reply_count: 9,
    upvote_count: 0,
    is_pinned: false,
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-3',
    era_id: 'era-3',
    author_id: 'user-3',
    author_name: 'Fatima Al-Rashid',
    author_avatar: null,
    author_tier: 'trusted',
    title: 'Timbuktu Manuscripts: The 400,000 Documents That Rewrote African Intellectual History',
    body: 'The Ahmed Baba Institute holds over 400,000 manuscripts covering mathematics, astronomy, medicine, and jurisprudence — most from the 13th–17th centuries. What strikes me most is the marginal commentary tradition: scholars would annotate texts across generations, creating layered intellectual dialogues spanning 200 years. This is not a "lost" civilization — it\'s a civilization whose documentation system we simply hadn\'t learned to read.',
    reply_count: 22,
    upvote_count: 0,
    is_pinned: false,
    created_at: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-4',
    era_id: 'era-4',
    author_id: 'user-4',
    author_name: 'Nkechi Adeyemi',
    author_avatar: null,
    author_tier: 'trusted',
    title: 'Benin Bronzes: Repatriation Is Only Half the Story',
    body: 'The ongoing repatriation debate focuses on physical objects, but the deeper issue is interpretive sovereignty. The Benin bronzes were not decorative art — they were a state archive. Each plaque recorded a specific event, official, or diplomatic exchange. Without the oral tradition of the Igun Eronmwon guild to decode them, even returned bronzes remain partially mute. We need to repatriate the interpretive framework alongside the objects.',
    reply_count: 31,
    upvote_count: 0,
    is_pinned: false,
    created_at: new Date(Date.now() - 14 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 14 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-5',
    era_id: 'era-5',
    author_id: 'user-5',
    author_name: 'Yaw Darko',
    author_avatar: null,
    author_tier: 'new',
    title: 'My Grandfather Remembered the 1900 Yaa Asantewaa War — Here\'s What He Told Me',
    body: 'I\'m a first-generation contributor here. My grandfather, born 1898, described the Siege of Kumasi Fort from the perspective of a young boy hiding in the bush. His account differs from the British military record in one crucial detail: he said the women who fought were not a last resort — they had been drilling for months. The British account frames it as spontaneous. I\'m trying to find corroborating oral sources. Any guidance on methodology?',
    reply_count: 7,
    upvote_count: 0,
    is_pinned: false,
    created_at: new Date(Date.now() - 22 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 22 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-6',
    era_id: 'era-1',
    author_id: 'user-6',
    author_name: 'Prof. Chinwe Eze',
    author_avatar: null,
    author_tier: 'historian',
    title: 'Igbo "Stateless" Society Was Not Stateless — It Was Differently Governed',
    body: 'Colonial anthropologists labeled Igbo political organization "acephalous" — without a head. This framing has persisted in textbooks. But the Ozo title system, the Eze Nri sacred kingship, and the Ekpe secret society constituted a distributed governance network of remarkable sophistication. Power was not absent; it was deliberately distributed to prevent tyranny. This is a design choice, not a developmental gap.',
    reply_count: 18,
    upvote_count: 0,
    is_pinned: false,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const MOCK_REPLIES: Record<string, ForumReply[]> = {
  'mock-1': [
    { id: 'r1', post_id: 'mock-1', author_id: 'u1', author_name: 'Kwame Asante', author_avatar: null, author_tier: 'trusted', parent_reply_id: null, upvote_count: 0, body: 'This resonates deeply. I\'ve been working with Akan proverb collections from the Basel Mission archives (1840s) and the consistency you describe is striking — the same dynastic sequences appear encoded in what missionaries dismissed as "folk sayings."', created_at: new Date(Date.now() - 90 * 60 * 1000).toISOString() },
    { id: 'r2', post_id: 'mock-1', author_id: 'u2', author_name: 'Dr. Amara Osei', author_avatar: null, author_tier: 'historian', parent_reply_id: null, upvote_count: 0, body: 'Exactly — and the Basel Mission archives are underutilized precisely because researchers approach them looking for European observations, not for the Akan voices embedded within them. The missionaries recorded more than they realized.', created_at: new Date(Date.now() - 60 * 60 * 1000).toISOString() },
    { id: 'r3', post_id: 'mock-1', author_id: 'u3', author_name: 'Abena Frimpong', author_avatar: null, author_tier: 'new', parent_reply_id: null, upvote_count: 0, body: 'Is there a methodology guide for cross-referencing oral sources with colonial archives? I\'m working on a contribution about the Fante Confederation and struggling with source validation.', created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString() },
  ],
  'mock-3': [
    { id: 'r4', post_id: 'mock-3', author_id: 'u4', author_name: 'Ibrahim Touré', author_avatar: null, author_tier: 'elder', parent_reply_id: null, upvote_count: 0, body: 'The marginal commentary tradition you describe is called "hashiya" — it\'s a formal scholarly practice that spread from North Africa into the Sahel. Some of the Timbuktu hashiya chains can be traced through 8 generations of scholars. It\'s essentially a peer-review system encoded in manuscript margins.', created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() },
    { id: 'r5', post_id: 'mock-3', author_id: 'u5', author_name: 'Fatima Al-Rashid', author_avatar: null, author_tier: 'trusted', parent_reply_id: null, upvote_count: 0, body: 'Ibrahim — yes! I\'ve been trying to map the hashiya networks. Do you have access to the digitized Mamma Haidara collection? Some of the astronomy manuscripts there have commentary chains that reference scholars from Fez, Cairo, AND Timbuktu in the same margin.', created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() },
  ],
};

const MOCK_AMAS: HistorianAma[] = [
  {
    id: 'ama-1',
    historian_id: 'h1',
    historian_name: 'Prof. Ngozi Adichie-Okafor',
    historian_avatar: null,
    historian_tier: 'historian',
    title: 'Decolonizing the Archive: How to Read Colonial Records Against the Grain',
    description: 'I\'ve spent 20 years working with British colonial records in the PRO and the National Archives of Ghana. Ask me anything about methodology, source criticism, or how to surface African voices in documents designed to erase them.',
    era_id: null,
    era_name: 'Colonial Era',
    status: 'live',
    question_count: 23,
    scheduled_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    ended_at: null,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'ama-2',
    historian_id: 'h2',
    historian_name: 'Dr. Cheikh Diallo',
    historian_avatar: null,
    historian_tier: 'elder',
    title: 'The Mali Empire\'s Administrative System: What the Griots Preserved',
    description: 'My family has been griots for the Keita lineage for seven generations. I hold both a PhD in African History from SOAS and the oral tradition passed down from my great-great-grandfather who witnessed the fall of Segou. I\'ll answer questions about the intersection of oral and written sources for the Mande world.',
    era_id: null,
    era_name: 'Medieval West Africa',
    status: 'upcoming',
    question_count: 8,
    scheduled_at: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    ended_at: null,
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'ama-3',
    historian_id: 'h3',
    historian_name: 'Dr. Wanjiru Kamau',
    historian_avatar: null,
    historian_tier: 'historian',
    title: 'Mau Mau Oral Testimonies: Recording Living Memory Before It\'s Gone',
    description: 'I\'ve conducted over 340 oral history interviews with Mau Mau veterans and their families in Central Kenya. The youngest living participants are now in their 80s. This AMA is about methodology, ethics of oral history, and what these testimonies reveal that the colonial record deliberately suppressed.',
    era_id: null,
    era_name: 'Independence Era',
    status: 'ended',
    question_count: 41,
    scheduled_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    ended_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const MOCK_AMA_QUESTIONS: Record<string, AmaQuestion[]> = {
  'ama-1': [
    { id: 'q1', ama_id: 'ama-1', asker_id: 'u1', asker_name: 'Yaw Darko', asker_avatar: null, question: 'When a colonial record describes an African leader as "cooperative" or "friendly," how do you read that against the grain? What\'s actually being said?', answer: '"Cooperative" almost always means the person accepted terms under duress or was playing a longer game. Look at what they were given in return — if it\'s described as "gifts" rather than "payment," that\'s a tell. Cross-reference with any subsequent land records or taxation changes in their territory within 5 years.', is_answered: true, upvote_count: 17, answered_at: null, is_featured: false, created_at: new Date(Date.now() - 25 * 60 * 1000).toISOString() },
    { id: 'q2', ama_id: 'ama-1', asker_id: 'u2', asker_name: 'Abena Frimpong', asker_avatar: null, question: 'What\'s your process for verifying an oral account against a colonial document when they directly contradict each other?', answer: 'I don\'t try to adjudicate between them — I treat the contradiction itself as the data. Ask: what would each party have had to gain by their version? Colonial records had legal and administrative functions; oral accounts had social and identity functions. They\'re answering different questions.', is_answered: true, upvote_count: 24, answered_at: null, is_featured: true, created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString() },
    { id: 'q3', ama_id: 'ama-1', asker_id: 'u3', asker_name: 'Kofi Mensah', asker_avatar: null, question: 'Are there specific record series in the PRO that are systematically underused by African history researchers?', answer: null, is_answered: false, upvote_count: 9, answered_at: null, is_featured: false, created_at: new Date(Date.now() - 10 * 60 * 1000).toISOString() },
  ],
  'ama-2': [
    { id: 'q4', ama_id: 'ama-2', asker_id: 'u4', asker_name: 'Fatima Al-Rashid', asker_avatar: null, question: 'How do you handle the tension between your role as a griot (keeper of a specific lineage\'s history) and your role as an academic historian (committed to broader evidence)?', answer: null, is_answered: false, upvote_count: 12, answered_at: null, is_featured: false, created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() },
  ],
};

const MOCK_TRENDING_TOPICS = [
  { id: 't1', tag: 'Timbuktu Manuscripts', posts: 89, hot: true, change: '+34%' },
  { id: 't2', tag: 'Benin Bronzes', posts: 76, hot: true, change: '+28%' },
  { id: 't3', tag: 'Oral Tradition Methodology', posts: 54, hot: false, change: '+12%' },
  { id: 't4', tag: 'Saharan Trade Routes', posts: 47, hot: false, change: '+8%' },
  { id: 't5', tag: 'Mau Mau Testimonies', posts: 41, hot: false, change: '+5%' },
  { id: 't6', tag: 'Igbo Political Systems', posts: 38, hot: false, change: '+3%' },
];

const MOCK_GAP_CHALLENGES = [
  {
    id: 'gc1',
    title: 'Document the 1874 Siege of Kumasi from Asante Oral Sources',
    description: 'British military records dominate the narrative. We need oral accounts from Asante families to fill the civilian perspective.',
    era: 'Colonial Era',
    reward: 150,
    submissions: 3,
    deadline: '12 days left',
    difficulty: 'Advanced',
  },
  {
    id: 'gc2',
    title: 'Trace the Kola Nut Credit System Across Saharan Trade Routes',
    description: 'Multiple oral traditions reference kola nuts as promissory tokens. Connect the dots across Mali, Ghana, and Hausaland sources.',
    era: 'Medieval West Africa',
    reward: 200,
    submissions: 1,
    deadline: '8 days left',
    difficulty: 'Expert',
  },
  {
    id: 'gc3',
    title: 'Map the Hashiya Commentary Networks of Timbuktu Scholars',
    description: 'Identify the scholarly lineages embedded in manuscript margins across the Ahmed Baba Institute collection.',
    era: 'Timbuktu Golden Age',
    reward: 175,
    submissions: 5,
    deadline: '20 days left',
    difficulty: 'Intermediate',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, avatar, size = 36 }: { name: string | null; avatar: string | null; size?: number }) {
  const initials = name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
  const colors = ['#E05A47', '#F39C12', '#7B6FA3', '#008080', '#2C5E3B', '#E07B3A'];
  const colorIdx = name ? name.charCodeAt(0) % colors.length : 0;
  if (avatar) {
    return (
      <img src={avatar} alt={name ? `${name} avatar` : 'User avatar'}
        className="rounded-full object-cover flex-shrink-0 ring-2 ring-white"
        style={{ width: size, height: size }} />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-white text-white font-semibold"
      style={{ width: size, height: size, backgroundColor: colors[colorIdx], fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

// ─── Tier Badge ───────────────────────────────────────────────────────────────

function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) return null;
  const map: Record<string, { label: string; cls: string }> = {
    new: { label: 'New', cls: 'tier-new' },
    trusted: { label: 'Trusted', cls: 'tier-trusted' },
    historian: { label: 'Historian', cls: 'tier-historian' },
    elder: { label: 'Elder', cls: 'tier-elder' },
  };
  const meta = map[tier] || map.new;
  return <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${meta.cls}`}>{meta.label}</span>;
}

// ─── Mini Profile Hover Card ──────────────────────────────────────────────────

interface HoverProfile {
  name: string | null;
  avatar: string | null;
  tier: string | null;
  bio?: string | null;
}

function ProfileHoverCard({ profile, children }: { profile: HoverProfile; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 400);
  };
  const handleLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShow(false);
  };

  return (
    <div className="relative inline-block" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {children}
      {show && (
        <div className="absolute left-0 top-full mt-2 z-50 w-56 rounded-xl border shadow-xl p-3"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2.5 mb-2">
            <Avatar name={profile.name} avatar={profile.avatar} size={36} />
            <div>
              <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--foreground)' }}>
                {profile.name || 'Anonymous'}
              </p>
              <TierBadge tier={profile.tier} />
            </div>
          </div>
          {profile.bio && (
            <p className="text-xs leading-relaxed mb-2" style={{ color: 'var(--muted-foreground)' }}>
              {profile.bio}
            </p>
          )}
          <button className="w-full text-xs py-1.5 rounded-lg font-medium transition-all"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
            Follow
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Compose Box ──────────────────────────────────────────────────────────────

const CERTAINTY_OPTIONS = [
  { value: 'oral', label: '🗣 Oral', color: 'var(--certainty-oral)' },
  { value: 'sourced', label: '📚 Sourced', color: 'var(--certainty-sourced)' },
  { value: 'firsthand', label: '✋ Firsthand', color: 'var(--certainty-firsthand)' },
];

interface AttachedMedia {
  type: 'file' | 'url';
  name: string;
  preview?: string;
  url?: string;
  file?: File;
  mediaKind?: 'image' | 'video' | 'other';
}

interface ComposeBoxProps {
  eras: Era[];
  userId: string | null;
  userProfile: { name: string | null; avatar: string | null } | null;
  onPost: (title: string, body: string, eraId: string, mediaUrls?: string[]) => Promise<void>;
  posting: boolean;
  postError: string | null;
  postSuccess: boolean;
  onClearError: () => void;
}

function ComposeBox({ eras, userId, userProfile, onPost, posting, postError, postSuccess, onClearError }: ComposeBoxProps) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedEra, setSelectedEra] = useState('');
  const [certainty, setCertainty] = useState('oral');
  const [showMediaPanel, setShowMediaPanel] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [attachedMedia, setAttachedMedia] = useState<AttachedMedia[]>([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync selectedEra when eras load
  useEffect(() => {
    if (eras.length > 0 && !selectedEra) {
      setSelectedEra(eras[0].id);
    }
  }, [eras, selectedEra]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const preview = isImage ? URL.createObjectURL(file) : undefined;
    const mediaKind: 'image' | 'video' | 'other' = isImage ? 'image' : isVideo ? 'video' : 'other';

    // Upload to Supabase storage
    setUploadingMedia(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const ext = file.name.split('.').pop();
      const path = `community/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage.from('contribution-media').upload(path, file, { upsert: false });
      if (!error && data) {
        const { data: urlData } = supabase.storage.from('contribution-media').getPublicUrl(data.path);
        const publicUrl = urlData.publicUrl;
        setAttachedMedia(prev => [...prev, { type: 'file', name: file.name, preview: isImage ? publicUrl : preview, url: publicUrl, mediaKind }]);
      } else {
        // Fallback: use object URL for preview only
        setAttachedMedia(prev => [...prev, { type: 'file', name: file.name, preview, file, mediaKind }]);
      }
    } catch {
      setAttachedMedia(prev => [...prev, { type: 'file', name: file.name, preview, file, mediaKind }]);
    } finally {
      setUploadingMedia(false);
    }
    setShowMediaPanel(false);
    if (e.target) e.target.value = '';
  };

  const handleUrlAttach = () => {
    if (!mediaUrl.trim()) return;
    const url = mediaUrl.trim();
    const name = url.split('/').pop() || 'Link';
    const lower = url.toLowerCase();
    const mediaKind: 'image' | 'video' | 'other' =
      /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/.test(lower) ? 'image': /\.(mp4|webm|mov|avi|mkv)(\?|$)/.test(lower) || /youtube\.com|youtu\.be|vimeo\.com/.test(lower) ?'video' : 'other';
    setAttachedMedia(prev => [...prev, { type: 'url', name, url, mediaKind }]);
    setMediaUrl('');
    setShowMediaPanel(false);
  };

  const removeMedia = (idx: number) => {
    setAttachedMedia(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) return;
    const eraId = selectedEra || eras[0]?.id || '';
    if (!eraId) return;
    const mediaUrls = attachedMedia.filter(m => m.url).map(m => m.url!);
    await onPost(title.trim(), body.trim(), eraId, mediaUrls);
    // Only clear form on success (postSuccess will be true)
    setTitle('');
    setBody('');
    setAttachedMedia([]);
    setExpanded(false);
    setShowMediaPanel(false);
  };

  // Auto-collapse on success
  useEffect(() => {
    if (postSuccess) {
      setTitle('');
      setBody('');
      setAttachedMedia([]);
      setExpanded(false);
    }
  }, [postSuccess]);

  if (!userId) {
    return (
      <div className="rounded-xl border p-4 mb-4 flex items-center gap-3"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="w-9 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--muted)' }} />
        <a href="/sign-up-login" className="flex-1 text-sm rounded-lg px-4 py-2.5 cursor-pointer transition-colors"
          style={{ backgroundColor: 'var(--input)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
          Sign in to share your perspective…
        </a>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="rounded-xl border overflow-hidden transition-all duration-200"
        style={{ backgroundColor: 'var(--card)', borderColor: expanded ? 'var(--primary)' : 'var(--border)', boxShadow: expanded ? '0 0 0 2px #E05A4722' : 'none' }}>
        <div className="p-3 flex items-center gap-3">
          <Avatar name={userProfile?.name || null} avatar={userProfile?.avatar || null} size={36} />
          {!expanded ? (
            <button onClick={() => { setExpanded(true); onClearError(); }}
              className="flex-1 text-left text-sm px-4 py-2.5 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--input)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
              Share a historical perspective…
            </button>
          ) : (
            <div className="flex-1">
              <input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Discussion title…"
                className="w-full text-sm font-medium px-0 py-1 outline-none bg-transparent border-b mb-2"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }} />
            </div>
          )}
          {expanded && (
            <button onClick={() => { setExpanded(false); setShowMediaPanel(false); onClearError(); }} style={{ color: 'var(--muted-foreground)' }}>
              <X size={16} />
            </button>
          )}
        </div>

        {expanded && (
          <div className="px-3 pb-3">
            <textarea value={body} onChange={e => setBody(e.target.value)}
              placeholder="Share your thoughts, cite sources, or ask a question…"
              rows={4}
              className="w-full text-sm px-3 py-2.5 rounded-lg border outline-none resize-none mb-3"
              style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }} />

            {/* Attached media preview */}
            {attachedMedia.length > 0 && (
              <div className="mb-3 space-y-2">
                {/* Image previews */}
                {attachedMedia.filter(m => m.mediaKind === 'image' && (m.preview || m.url)).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachedMedia.map((m, i) => m.mediaKind === 'image' && (m.preview || m.url) ? (
                      <div key={i} className="relative group">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={m.preview || m.url} alt={m.name} className="w-20 h-20 rounded-lg object-cover border" style={{ borderColor: 'var(--border)' }} />
                        <button onClick={() => removeMedia(i)}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff' }}>
                          <X size={10} />
                        </button>
                      </div>
                    ) : null)}
                  </div>
                )}
                {/* Non-image media chips */}
                {attachedMedia.filter(m => m.mediaKind !== 'image').map((m, i) => {
                  const realIdx = attachedMedia.indexOf(m);
                  return (
                    <div key={i} className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs"
                      style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      {m.mediaKind === 'video'
                        ? <Video size={12} style={{ color: 'var(--primary)' }} />
                        : m.type === 'url'
                          ? <Link2 size={12} style={{ color: 'var(--primary)' }} />
                          : <Paperclip size={12} style={{ color: 'var(--primary)' }} />
                      }
                      <span className="max-w-[120px] truncate">{m.name}</span>
                      <button onClick={() => removeMedia(realIdx)} className="ml-1 hover:opacity-70" style={{ color: 'var(--muted-foreground)' }}>
                        <X size={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Inline media panel */}
            {showMediaPanel && (
              <div className="mb-3 rounded-lg border p-3 space-y-2"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Attach Media</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingMedia}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                    <Image size={12} style={{ color: 'var(--primary)' }} />
                    {uploadingMedia ? 'Uploading…' : 'Upload Image / Video'}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFileSelect} />
                </div>
                <div className="flex gap-2">
                  <input
                    value={mediaUrl}
                    onChange={e => setMediaUrl(e.target.value)}
                    placeholder="Paste image or video URL…"
                    className="flex-1 text-xs px-3 py-1.5 rounded-lg border outline-none"
                    style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUrlAttach(); } }}
                  />
                  <button
                    onClick={handleUrlAttach}
                    disabled={!mediaUrl.trim()}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                    style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    Add
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
                <Hash size={12} style={{ color: 'var(--primary)' }} />
                <select value={selectedEra} onChange={e => setSelectedEra(e.target.value)}
                  className="bg-transparent outline-none text-xs cursor-pointer"
                  style={{ color: 'var(--foreground)' }}>
                  {eras.length === 0 && <option value="">Loading eras…</option>}
                  {eras.map(era => <option key={era.id} value={era.id}>{era.name}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
                <CheckCircle size={12} style={{ color: 'var(--primary)' }} />
                <select value={certainty} onChange={e => setCertainty(e.target.value)}
                  className="bg-transparent outline-none text-xs cursor-pointer"
                  style={{ color: 'var(--foreground)' }}>
                  {CERTAINTY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <button
                onClick={() => setShowMediaPanel(prev => !prev)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-colors"
                style={{
                  backgroundColor: showMediaPanel ? '#E05A4715' : 'var(--muted)',
                  borderColor: showMediaPanel ? 'var(--primary)' : 'var(--border)',
                  color: showMediaPanel ? 'var(--primary)' : 'var(--muted-foreground)',
                }}>
                <Image size={12} />
                Photo/Video{attachedMedia.length > 0 ? ` (${attachedMedia.length})` : ''}
              </button>

              <div className="flex-1" />

              <button onClick={handleSubmit}
                disabled={posting || !title.trim() || !body.trim() || eras.length === 0}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 transition-all"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                <Send size={13} />
                {posting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Feedback toasts rendered outside the compose box so they persist after collapse */}
      {postError && (
        <div className="mt-2 px-4 py-3 rounded-lg border text-sm flex items-center gap-2"
          style={{ backgroundColor: '#E05A4715', borderColor: '#E05A4740', color: 'var(--primary)' }}>
          <X size={14} />
          {postError}
          <button onClick={onClearError} className="ml-auto opacity-60 hover:opacity-100">
            <X size={12} />
          </button>
        </div>
      )}
      {postSuccess && (
        <div className="mt-2 px-4 py-3 rounded-lg border text-sm flex items-center gap-2"
          style={{ backgroundColor: '#2C5E3B15', borderColor: '#2C5E3B40', color: '#2C5E3B' }}>
          <CheckCircle size={14} />
          Post submitted successfully! It will appear in the feed.
        </div>
      )}
    </div>
  );
}

// ─── Mock Reaction Bar ────────────────────────────────────────────────────────

const REACTION_PRESETS: Record<string, { insightful: number; citation: number; eyeopening: number }> = {
  'mock-1': { insightful: 34, citation: 12, eyeopening: 19 },
  'mock-2': { insightful: 21, citation: 28, eyeopening: 8 },
  'mock-3': { insightful: 67, citation: 41, eyeopening: 53 },
  'mock-4': { insightful: 89, citation: 22, eyeopening: 71 },
  'mock-5': { insightful: 14, citation: 9, eyeopening: 11 },
  'mock-6': { insightful: 45, citation: 17, eyeopening: 38 },
};

function MockReactionBar({ postId }: { postId: string }) {
  const preset = REACTION_PRESETS[postId] || { insightful: 5, citation: 3, eyeopening: 4 };
  const [counts, setCounts] = useState(preset);
  const [active, setActive] = useState<Record<string, boolean>>({});

  const toggle = (key: keyof typeof preset) => {
    setActive(prev => {
      const next = { ...prev, [key]: !prev[key] };
      setCounts(c => ({ ...c, [key]: c[key] + (next[key] ? 1 : -1) }));
      return next;
    });
  };

  const reactions = [
    { key: 'insightful' as const, emoji: '💡', label: 'Insightful', count: counts.insightful },
    { key: 'citation' as const, emoji: '📚', label: 'Needs Citation', count: counts.citation },
    { key: 'eyeopening' as const, emoji: '👁️', label: 'Eye-opening', count: counts.eyeopening },
  ];

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {reactions.map(r => (
        <button key={r.key} onClick={() => toggle(r.key)}
          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all border"
          style={{
            backgroundColor: active[r.key] ? '#E05A4715' : 'transparent',
            borderColor: active[r.key] ? 'var(--primary)' : 'var(--border)',
            color: active[r.key] ? 'var(--primary)' : 'var(--muted-foreground)',
          }}>
          <span>{r.emoji}</span>
          <span className="font-medium">{r.count}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Post Card ────────────────────────────────────────────────────────────────

interface PostCardProps {
  post: ForumPost;
  eraName?: string;
  userId: string | null;
  onExpand: (id: string) => void;
  expanded: boolean;
  replies: ForumReply[];
  loadingReplies: boolean;
  onReply: (postId: string, body: string) => Promise<void>;
  isMock?: boolean;
}

function PostCard({ post, eraName, userId, onExpand, expanded, replies, loadingReplies, onReply, isMock }: PostCardProps) {
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mockReplies = isMock ? (MOCK_REPLIES[post.id] || []) : replies;

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    await onReply(post.id, replyText.trim());
    setReplyText('');
    setSubmitting(false);
  };

  const mediaUrls = post.media_urls || [];
  const imageUrls = mediaUrls.filter(u => /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(u) || (!u.includes('youtu') && !u.includes('vimeo') && !u.match(/\.(mp4|webm|mov)(\?|$)/i)));
  const videoUrls = mediaUrls.filter(u => /\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(u));
  const embedUrls = mediaUrls.filter(u => /youtube\.com|youtu\.be|vimeo\.com/.test(u));

  function getEmbedUrl(url: string): string {
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  }

  return (
    <article className="rounded-xl border overflow-hidden card-hover mb-3"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <ProfileHoverCard profile={{ name: post.author_name, avatar: post.author_avatar, tier: post.author_tier }}>
            <Avatar name={post.author_name} avatar={post.author_avatar} size={40} />
          </ProfileHoverCard>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                {post.author_name || 'Anonymous'}
              </span>
              <TierBadge tier={post.author_tier} />
              {eraName && (
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: '#E05A4715', color: 'var(--primary)' }}>
                  <Hash size={10} />
                  {eraName}
                </span>
              )}
              <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
                {timeAgo(post.created_at)}
              </span>
            </div>

            <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
              {post.is_pinned && <span className="mr-1.5 text-xs px-1.5 py-0.5 rounded font-mono" style={{ backgroundColor: '#C17A3E22', color: 'var(--primary)' }}>📌 pinned</span>}
              {post.title}
            </h3>
            <p className="text-sm leading-relaxed line-clamp-3" style={{ color: 'var(--muted-foreground)' }}>
              {post.body}
            </p>
          </div>
        </div>

        {/* Media display in feed */}
        {mediaUrls.length > 0 && (
          <div className="mt-3 space-y-2">
            {/* Images */}
            {imageUrls.length > 0 && (
              <div className={`grid gap-2 ${imageUrls.length === 1 ? 'grid-cols-1' : imageUrls.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {imageUrls.slice(0, 3).map((url, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden" style={{ aspectRatio: imageUrls.length === 1 ? '16/9' : '1/1' }}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={url} alt={`Post image ${i + 1}`}
                      className="w-full h-full object-cover"
                      style={{ maxHeight: imageUrls.length === 1 ? 320 : 160 }} />
                    {imageUrls.length > 3 && i === 2 && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-lg"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <span className="text-white font-bold text-lg">+{imageUrls.length - 3}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Videos */}
            {videoUrls.map((url, i) => (
              <div key={i} className="rounded-lg overflow-hidden">
                <video controls className="w-full max-h-72 rounded-lg" style={{ backgroundColor: '#000' }}>
                  <source src={url} />
                  Your browser does not support video playback.
                </video>
              </div>
            ))}
            {/* Embeds (YouTube/Vimeo) */}
            {embedUrls.map((url, i) => (
              <div key={i} className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={getEmbedUrl(url)}
                  className="absolute inset-0 w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`Embedded video ${i + 1}`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
          {isMock
            ? <MockReactionBar postId={post.id} />
            : <ReactionButtons contentType="forum_post" contentId={post.id} compact />
          }
          <div className="flex-1" />
          <button onClick={() => onExpand(post.id)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--muted-foreground)', backgroundColor: expanded ? 'var(--muted)' : 'transparent' }}>
            <MessageSquare size={13} />
            {isMock ? mockReplies.length : post.reply_count} {(isMock ? mockReplies.length : post.reply_count) === 1 ? 'reply' : 'replies'}
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t px-4 pb-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--secondary)' }}>
          {loadingReplies && !isMock ? (
            <div className="py-4 space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: 'var(--muted)' }} />
                  <div className="flex-1 h-10 rounded-lg animate-pulse" style={{ backgroundColor: 'var(--muted)' }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="pt-3 space-y-3">
              {mockReplies.length === 0 && (
                <p className="text-xs text-center py-2" style={{ color: 'var(--muted-foreground)' }}>No replies yet — be the first!</p>
              )}
              {mockReplies.map(reply => (
                <div key={reply.id} className="flex gap-2.5">
                  <Avatar name={reply.author_name} avatar={reply.author_avatar} size={28} />
                  <div className="flex-1 min-w-0 rounded-lg px-3 py-2" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                        {reply.author_name || 'Anonymous'}
                      </span>
                      <TierBadge tier={reply.author_tier} />
                      <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
                        {timeAgo(reply.created_at)}
                      </span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{reply.body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {userId ? (
            <div className="flex gap-2.5 mt-3">
              <Avatar name={null} avatar={null} size={28} />
              <div className="flex-1 flex gap-2">
                <input value={replyText} onChange={e => setReplyText(e.target.value)}
                  placeholder="Write a reply…"
                  className="flex-1 text-xs px-3 py-2 rounded-lg border outline-none"
                  style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }} />
                <button onClick={handleReply} disabled={submitting || !replyText.trim()}
                  className="px-3 py-2 rounded-lg text-xs font-medium disabled:opacity-50 flex items-center gap-1"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  <Send size={11} />
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs mt-3 text-center" style={{ color: 'var(--muted-foreground)' }}>
              <a href="/sign-up-login" style={{ color: 'var(--primary)' }}>Sign in</a> to reply
            </p>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Gap Challenges Section ───────────────────────────────────────────────────

function GapChallengesSection() {
  const difficultyColor: Record<string, string> = {
    Intermediate: '#F39C12',
    Advanced: '#E07B3A',
    Expert: '#E05A47',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Gap Challenges</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            Earn reputation points by filling documented gaps in the historical record.
          </p>
        </div>
        <span className="text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ backgroundColor: '#E05A4715', color: 'var(--primary)' }}>
          {MOCK_GAP_CHALLENGES.length} open
        </span>
      </div>

      {MOCK_GAP_CHALLENGES.map(challenge => (
        <div key={challenge.id} className="rounded-xl border overflow-hidden card-hover"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: '#E05A4715', color: 'var(--primary)' }}>
                    #{challenge.era}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: `${difficultyColor[challenge.difficulty]}20`, color: difficultyColor[challenge.difficulty] }}>
                    {challenge.difficulty}
                  </span>
                </div>
                <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
                  {challenge.title}
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {challenge.description}
                </p>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center"
                  style={{ backgroundColor: '#F39C1215', border: '1px solid #F39C1230' }}>
                  <Award size={14} style={{ color: '#F39C12' }} />
                  <span className="text-xs font-bold mt-0.5" style={{ color: '#A06800' }}>{challenge.reward}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <Users size={11} />
                {challenge.submissions} submission{challenge.submissions !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <Clock size={11} />
                {challenge.deadline}
              </span>
              <div className="flex-1" />
              <button className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Accept Challenge
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Trending Feed Section ────────────────────────────────────────────────────

function TrendingFeedSection() {
  const trendingPosts = MOCK_POSTS.slice().sort((a, b) => {
    const ra = REACTION_PRESETS[a.id] || { insightful: 0, citation: 0, eyeopening: 0 };
    const rb = REACTION_PRESETS[b.id] || { insightful: 0, citation: 0, eyeopening: 0 };
    const totalA = ra.insightful + ra.citation + ra.eyeopening;
    const totalB = rb.insightful + rb.citation + rb.eyeopening;
    return totalB - totalA;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Flame size={16} style={{ color: 'var(--primary)' }} />
        <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Trending This Week</h2>
      </div>
      {trendingPosts.map((post, idx) => {
        const reactions = REACTION_PRESETS[post.id] || { insightful: 0, citation: 0, eyeopening: 0 };
        const total = reactions.insightful + reactions.citation + reactions.eyeopening;
        return (
          <div key={post.id} className="rounded-xl border overflow-hidden card-hover p-4"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="flex items-start gap-3">
              <span className="text-2xl font-black w-8 text-center flex-shrink-0 leading-none mt-1"
                style={{ color: idx === 0 ? 'var(--primary)' : 'var(--muted-foreground)', opacity: idx === 0 ? 1 : 0.4 }}>
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Avatar name={post.author_name} avatar={post.author_avatar} size={22} />
                  <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{post.author_name}</span>
                  <TierBadge tier={post.author_tier} />
                  <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>{timeAgo(post.created_at)}</span>
                </div>
                <h3 className="text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>{post.title}</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <ThumbsUp size={11} />
                    {total} reactions
                  </span>
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <MessageSquare size={11} />
                    {post.reply_count} replies
                  </span>
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <Eye size={11} />
                    {(total * 4 + post.reply_count * 7)} views
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Right Sidebar ────────────────────────────────────────────────────────────

interface RightSidebarProps {
  eras: Era[];
  amas: HistorianAma[];
  userId: string | null;
}

function RightSidebar({ eras, amas, userId }: RightSidebarProps) {
  const displayAmas = amas.length > 0 ? amas : MOCK_AMAS;
  const liveAma = displayAmas.find(a => a.status === 'live');
  const upcomingAma = displayAmas.find(a => a.status === 'upcoming');

  const [followed, setFollowed] = useState<Record<number, boolean>>({});

  const latestPosts = MOCK_POSTS.slice().sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  ).slice(0, 4);

  const activeUsers = [
    { name: 'Dr. Amara Osei', tier: 'historian', action: 'Posted 2m ago', avatar: null },
    { name: 'Ibrahim Touré', tier: 'elder', action: 'Replied 5m ago', avatar: null },
    { name: 'Fatima Al-Rashid', tier: 'trusted', action: 'Active in AMA', avatar: null },
    { name: 'Kofi Mensah', tier: 'elder', action: 'Posted 18m ago', avatar: null },
    { name: 'Prof. Chinwe Eze', tier: 'historian', action: 'Replied 31m ago', avatar: null },
  ];

  return (
    <aside className="w-72 flex-shrink-0 hidden lg:block">
      <div className="sticky top-20 space-y-4">

        {/* Trending Topics */}
        <div className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
            <Flame size={14} style={{ color: 'var(--primary)' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>
              Trending Topics
            </span>
          </div>
          <div className="p-2">
            {MOCK_TRENDING_TOPICS.map((topic, i) => (
              <div key={topic.id}
                className="flex items-center justify-between px-2 py-2 rounded-lg cursor-pointer transition-colors"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-mono w-4 text-right flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--foreground)' }}>
                      #{topic.tag}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {topic.posts} posts · <span style={{ color: '#2C5E3B' }}>{topic.change}</span>
                    </p>
                  </div>
                </div>
                {topic.hot && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: '#E05A4715', color: 'var(--primary)' }}>
                    🔥
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Latest Posts */}
        <div className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
            <Sparkles size={14} style={{ color: 'var(--primary)' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>
              Latest Posts
            </span>
          </div>
          <div className="p-2">
            {latestPosts.map(post => (
              <div key={post.id}
                className="flex items-start gap-2.5 px-2 py-2.5 rounded-lg cursor-pointer transition-colors"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                <Avatar name={post.author_name} avatar={post.author_avatar} size={28} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium leading-snug line-clamp-2" style={{ color: 'var(--foreground)' }}>
                    {post.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {post.author_name?.split(' ')[0]}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {timeAgo(post.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Users */}
        <div className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
            <Users size={14} style={{ color: 'var(--primary)' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>
              Active Users
            </span>
            <span className="ml-auto flex items-center gap-1 text-xs" style={{ color: '#2C5E3B' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
              {activeUsers.length} online
            </span>
          </div>
          <div className="p-2">
            {activeUsers.map((user, i) => (
              <div key={i} className="flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                <div className="relative flex-shrink-0">
                  <Avatar name={user.name} avatar={user.avatar} size={30} />
                  <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-green-500 border border-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--foreground)' }}>{user.name}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{user.action}</p>
                </div>
                <TierBadge tier={user.tier} />
              </div>
            ))}
          </div>
        </div>

        {/* Active AMA (if live or upcoming) */}
        {(liveAma || upcomingAma) && (
          <div className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b flex items-center gap-2"
              style={{ borderColor: 'var(--border)', backgroundColor: liveAma ? '#E05A4710' : 'transparent' }}>
              <Radio size={14} style={{ color: liveAma ? 'var(--primary)' : 'var(--muted-foreground)' }} />
              <span className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: liveAma ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                {liveAma ? '🔴 Live AMA' : '📅 Upcoming AMA'}
              </span>
            </div>
            <div className="p-3">
              {(() => {
                const ama = liveAma || upcomingAma!;
                return (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar name={ama.historian_name} avatar={ama.historian_avatar} size={32} />
                      <div>
                        <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                          {ama.historian_name || 'Historian'}
                        </p>
                        <TierBadge tier={ama.historian_tier} />
                      </div>
                    </div>
                    <p className="text-xs font-medium mb-1 leading-snug" style={{ color: 'var(--foreground)' }}>{ama.title}</p>
                    <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      {ama.question_count} questions · {ama.era_name}
                    </p>
                    <button className="w-full text-xs py-1.5 rounded-lg font-medium transition-all"
                      style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                      {liveAma ? 'Join AMA' : 'Set Reminder'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Community stats */}
        <div className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
            <Shield size={14} style={{ color: 'var(--primary)' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>
              Community Stats
            </span>
          </div>
          <div className="p-3 grid grid-cols-2 gap-2">
            {[
              { label: 'Members', value: '2,847' },
              { label: 'Posts Today', value: '143' },
              { label: 'AMAs Held', value: '38' },
              { label: 'Gaps Filled', value: '612' },
            ].map(stat => (
              <div key={stat.label} className="rounded-lg p-2 text-center"
                style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{stat.value}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </aside>
  );
}

// ─── Activity Summary Strip ───────────────────────────────────────────────────

interface ActivityStripProps {
  userId: string | null;
  userName: string | null;
  userAvatar: string | null;
  userTier: string | null;
}

function ActivityStrip({ userId, userName, userAvatar, userTier }: ActivityStripProps) {
  if (!userId) return null;
  return (
    <div className="rounded-xl border px-4 py-3 mb-4 flex items-center gap-4 flex-wrap"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2.5">
        <Avatar name={userName} avatar={userAvatar} size={34} />
        <div>
          <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{userName || 'You'}</p>
          <TierBadge tier={userTier} />
        </div>
      </div>
      <div className="h-6 w-px hidden sm:block" style={{ backgroundColor: 'var(--border)' }} />
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
          <Zap size={13} style={{ color: 'var(--accent)' }} />
          <span><strong style={{ color: 'var(--foreground)' }}>7</strong> day streak</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
          <Bell size={13} style={{ color: 'var(--primary)' }} />
          <span><strong style={{ color: 'var(--foreground)' }}>3</strong> mentions</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
          <Star size={13} style={{ color: '#F39C12' }} />
          <span><strong style={{ color: 'var(--foreground)' }}>2</strong> open challenges</span>
        </div>
      </div>
    </div>
  );
}

// ─── New Posts Pill ───────────────────────────────────────────────────────────

function NewPostsPill({ count, onDismiss }: { count: number; onDismiss: () => void }) {
  if (count === 0) return null;
  return (
    <div className="sticky top-20 z-30 flex justify-center pointer-events-none mb-3">
      <button onClick={onDismiss}
        className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium shadow-lg transition-all hover:scale-105 active:scale-95"
        style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
        <Sparkles size={14} />
        {count} new post{count > 1 ? 's' : ''} — click to refresh
      </button>
    </div>
  );
}

// ─── AMA Section ─────────────────────────────────────────────────────────────

function AmaSection({ amas, userId }: { amas: HistorianAma[]; userId: string | null }) {
  const [expandedAma, setExpandedAma] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Record<string, AmaQuestion[]>>({});
  const [questionText, setQuestionText] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const displayAmas = amas.length > 0 ? amas : MOCK_AMAS;
  const isMock = amas.length === 0;

  const handleExpand = async (amaId: string) => {
    if (expandedAma === amaId) { setExpandedAma(null); return; }
    setExpandedAma(amaId);
    if (!questions[amaId]) {
      if (isMock) {
        setQuestions(prev => ({ ...prev, [amaId]: MOCK_AMA_QUESTIONS[amaId] || [] }));
      } else {
        const data = await getAmaQuestions(amaId);
        setQuestions(prev => ({ ...prev, [amaId]: data }));
      }
    }
  };

  const handleSubmitQuestion = async (amaId: string) => {
    if (!userId || !questionText[amaId]?.trim()) return;
    setSubmitting(true);
    if (!isMock) {
      const q = await submitAmaQuestion({ amaId, askerId: userId, question: questionText[amaId].trim() });
      if (q) {
        setQuestions(prev => ({ ...prev, [amaId]: [...(prev[amaId] || []), q] }));
        setQuestionText(prev => ({ ...prev, [amaId]: '' }));
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <BookOpen size={16} style={{ color: 'var(--primary)' }} />
        <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Historian AMAs</h2>
        <span className="text-xs px-2 py-0.5 rounded-full ml-auto"
          style={{ backgroundColor: '#E05A4715', color: 'var(--primary)' }}>
          {displayAmas.filter(a => a.status === 'live').length} live now
        </span>
      </div>

      {displayAmas.map(ama => (
        <article key={ama.id} className="rounded-xl border overflow-hidden card-hover"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Avatar name={ama.historian_name} avatar={ama.historian_avatar} size={44} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                    {ama.historian_name || 'Historian'}
                  </span>
                  <TierBadge tier={ama.historian_tier} />
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: ama.status === 'live' ? '#E05A4720' : ama.status === 'upcoming' ? '#F39C1220' : 'var(--muted)',
                      color: ama.status === 'live' ? 'var(--primary)' : ama.status === 'upcoming' ? '#A06800' : 'var(--muted-foreground)',
                    }}>
                    {ama.status === 'live' ? '🔴 Live' : ama.status === 'upcoming' ? '📅 Upcoming' : '✓ Ended'}
                  </span>
                </div>
                <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>{ama.title}</h3>
                <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
                  {ama.description}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <MessageSquare size={12} />
                    {ama.question_count} questions
                  </span>
                  {ama.era_name && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      <Hash size={11} />
                      {ama.era_name}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => handleExpand(ama.id)}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-all flex-shrink-0"
                style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                {expandedAma === ama.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {expandedAma === ama.id ? 'Hide' : 'View'}
              </button>
            </div>
          </div>

          {expandedAma === ama.id && (
            <div className="border-t px-4 pb-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--secondary)' }}>
              <div className="pt-3 space-y-3">
                {(questions[ama.id] || []).map(q => (
                  <div key={q.id} className="rounded-lg p-3" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Avatar name={q.asker_name} avatar={q.asker_avatar} size={22} />
                      <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>
                        {q.asker_name || 'Anonymous'}
                      </span>
                      {q.upvote_count > 0 && (
                        <span className="flex items-center gap-1 text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
                          <ThumbsUp size={10} /> {q.upvote_count}
                        </span>
                      )}
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        {timeAgo(q.created_at)}
                      </span>
                    </div>
                    <p className="text-xs font-medium mb-1 flex items-start gap-1.5" style={{ color: 'var(--foreground)' }}>
                      <Quote size={11} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
                      {q.question}
                    </p>
                    {q.answer && (
                      <div className="mt-2 pl-3 border-l-2" style={{ borderColor: 'var(--primary)' }}>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                          {q.answer}
                        </p>
                      </div>
                    )}
                    {!q.answer && (
                      <p className="text-xs mt-1 italic" style={{ color: 'var(--muted-foreground)' }}>Awaiting answer…</p>
                    )}
                  </div>
                ))}
                {(questions[ama.id] || []).length === 0 && (
                  <p className="text-xs text-center py-2" style={{ color: 'var(--muted-foreground)' }}>No questions yet.</p>
                )}
              </div>
              {userId && ama.status !== 'ended' && (
                <div className="flex gap-2 mt-3">
                  <input value={questionText[ama.id] || ''} onChange={e => setQuestionText(prev => ({ ...prev, [ama.id]: e.target.value }))}
                    placeholder="Ask a question…"
                    className="flex-1 text-xs px-3 py-2 rounded-lg border outline-none"
                    style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmitQuestion(ama.id); }} />
                  <button onClick={() => handleSubmitQuestion(ama.id)} disabled={submitting || !questionText[ama.id]?.trim()}
                    className="px-3 py-2 rounded-lg text-xs font-medium disabled:opacity-50"
                    style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    <Send size={11} />
                  </button>
                </div>
              )}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SocialCommunityPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [eras, setEras] = useState<Era[]>([]);
  const [amas, setAmas] = useState<HistorianAma[]>([]);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [selectedEra, setSelectedEra] = useState<Era | null>(null);
  const [activeTab, setActiveTab] = useState('feed');
  const [expandedPost, setExpandedPost] = useState<string | null>(null);
  const [replies, setReplies] = useState<Record<string, ForumReply[]>>({});
  const [loadingReplies, setLoadingReplies] = useState<string | null>(null);
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [userProfile, setUserProfile] = useState<{ name: string | null; avatar: string | null; tier: string | null } | null>(null);
  const eraMapRef = useRef<Record<string, string>>({});

  // Post submission state
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [postSuccess, setPostSuccess] = useState(false);

  // Load eras and AMAs on mount
  useEffect(() => {
    getEras().then(data => {
      setEras(data);
      const map: Record<string, string> = {};
      data.forEach(e => { map[e.id] = e.name; });
      eraMapRef.current = map;
    });
    getHistorianAmas().then(setAmas);
  }, []);

  // Load user profile
  useEffect(() => {
    if (!user) { setUserProfile(null); return; }
    const supabase = createClient();
    supabase.from('user_profiles').select('full_name, avatar_url, tier').eq('id', user.id).single()
      .then(({ data }) => {
        if (data) setUserProfile({ name: data.full_name, avatar: data.avatar_url, tier: data.tier });
      });
  }, [user]);

  // Load posts — does NOT depend on eras being loaded first
  const loadPosts = useCallback(async () => {
    if (activeTab !== 'feed') return;
    setLoadingPosts(true);
    setNewPostsCount(0);
    const data = await getForumPosts(selectedEra?.id);
    setPosts(data);
    setLoadingPosts(false);
  }, [selectedEra, activeTab]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Real-time subscription — new posts appear instantly in the feed
  useEffect(() => {
    if (activeTab !== 'feed') return;
    const supabase = createClient();

    const channelConfig: Record<string, unknown> = {
      event: 'INSERT',
      schema: 'public',
      table: 'era_forum_posts',
    };
    // Only filter by era when one is selected; otherwise subscribe to all posts
    if (selectedEra) {
      channelConfig.filter = `era_id=eq.${selectedEra.id}`;
    }

    const channel = supabase
      .channel('community-realtime-posts')
      .on('postgres_changes', channelConfig as Parameters<ReturnType<typeof supabase.channel>['on']>[1], async (payload) => {
        const newRow = payload.new as Record<string, unknown>;
        if (!newRow?.id) return;

        // Fetch author profile to build a complete ForumPost object
        let authorName: string | null = null;
        let authorAvatar: string | null = null;
        let authorTier: string | null = null;
        if (newRow.author_id) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('full_name, avatar_url, tier')
            .eq('id', newRow.author_id)
            .single();
          if (profile) {
            authorName = profile.full_name ?? null;
            authorAvatar = profile.avatar_url ?? null;
            authorTier = profile.tier ?? null;
          }
        }

        const incomingPost: ForumPost = {
          id: newRow.id as string,
          era_id: newRow.era_id as string,
          author_id: (newRow.author_id as string) ?? null,
          title: newRow.title as string,
          body: newRow.body as string,
          upvote_count: (newRow.upvote_count as number) ?? 0,
          reply_count: (newRow.reply_count as number) ?? 0,
          is_pinned: (newRow.is_pinned as boolean) ?? false,
          created_at: newRow.created_at as string,
          updated_at: (newRow.updated_at as string) ?? (newRow.created_at as string),
          author_name: authorName,
          author_avatar: authorAvatar,
          author_tier: authorTier,
          media_urls: (newRow.media_urls as string[]) ?? [],
        };

        // If the post was created by the current user it was already prepended
        // optimistically in handlePost — skip to avoid duplicates
        setPosts(prev => {
          if (prev.some(p => p.id === incomingPost.id)) return prev;
          return [incomingPost, ...prev];
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selectedEra, activeTab, user?.id]);

  const handleExpandPost = async (postId: string) => {
    if (expandedPost === postId) { setExpandedPost(null); return; }
    setExpandedPost(postId);
    if (postId.startsWith('mock-')) return;
    if (!replies[postId]) {
      setLoadingReplies(postId);
      const data = await getForumReplies(postId);
      setReplies(prev => ({ ...prev, [postId]: data }));
      setLoadingReplies(null);
    }
  };

  const handleReply = async (postId: string, body: string) => {
    if (!user || postId.startsWith('mock-')) return;
    const reply = await createForumReply({ postId, authorId: user.id, body });
    if (reply) {
      setReplies(prev => ({ ...prev, [postId]: [...(prev[postId] || []), reply] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, reply_count: p.reply_count + 1 } : p));
    }
  };

  // ── Core post submission ──────────────────────────────────────────────────
  const handlePost = async (title: string, body: string, eraId: string, mediaUrls?: string[]) => {
    if (!user) return;

    setPosting(true);
    setPostError(null);
    setPostSuccess(false);

    const { post, error } = await createForumPost({
      eraId,
      authorId: user.id,
      title,
      body,
      mediaUrls,
    });

    setPosting(false);

    if (error || !post) {
      setPostError(error || 'Failed to submit post. Please try again.');
      return;
    }

    // Optimistic: prepend new post to feed immediately
    setPosts(prev => [post, ...prev]);
    setPostSuccess(true);

    // Auto-clear success banner after 4 seconds
    setTimeout(() => setPostSuccess(false), 4000);
  };

  const handleSelectEra = (era: Era | null) => {
    setSelectedEra(era);
    setExpandedPost(null);
    setNewPostsCount(0);
  };

  // Show mock posts only when DB returned nothing AND not loading
  const displayPosts = !loadingPosts && posts.length === 0 ? MOCK_POSTS : posts;
  const usingMockPosts = posts.length === 0 && !loadingPosts;

  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  const handleStartProject = () => {
    if (user) {
      router.push('/contribution-submission');
    } else {
      setShowSignInPrompt(true);
    }
  };

  const tabs = [
    { id: 'feed', label: 'All Discussions', icon: Flame },
    { id: 'amas', label: 'AMAs', icon: BookOpen },
    { id: 'challenges', label: 'Challenges', icon: Target },
    { id: 'trending', label: 'Trending', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      {/* Sign-in gate modal */}
      {showSignInPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div className="rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: 'rgba(193,122,62,0.12)', border: '1px solid rgba(193,122,62,0.25)' }}>
              <PlusCircle size={26} style={{ color: 'var(--primary)' }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
              Sign in to start a project
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
              You need an account to start a project. You can browse the community freely — no sign-up required.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                href="/sign-up-login"
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                <LogIn size={15} />
                Sign in or create account
              </Link>
              <button
                onClick={() => setShowSignInPrompt(false)}
                className="px-6 py-2.5 rounded-lg text-sm font-medium border transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
              >
                Continue browsing
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Community</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Discuss history, ask historians, and connect with contributors across eras.
            </p>
          </div>
          <button
            onClick={handleStartProject}
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95 whitespace-nowrap"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            <PlusCircle size={15} />
            Start a Project
          </button>
        </div>

        <div className="flex gap-6">
          {/* ── Main Feed Column ── */}
          <main className="flex-1 min-w-0">
            <ActivityStrip
              userId={user?.id || null}
              userName={userProfile?.name || null}
              userAvatar={userProfile?.avatar || null}
              userTier={userProfile?.tier || null}
            />

            {/* Tab navigation */}
            <div className="flex items-center gap-1 mb-4 border-b overflow-x-auto"
              style={{ borderColor: 'var(--border)' }}>
              {tabs.map(tab => {
                const TabIcon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className="flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap transition-all border-b-2 -mb-px"
                    style={{
                      borderColor: active ? 'var(--primary)' : 'transparent',
                      color: active ? 'var(--primary)' : 'var(--muted-foreground)',
                      fontWeight: active ? 600 : 400,
                    }}>
                    <TabIcon size={14} />
                    {tab.label}
                  </button>
                );
              })}

              {/* Era filter (inline, right side) */}
              <div className="ml-auto flex-shrink-0 pb-1">
                <select value={selectedEra?.id || ''}
                  onChange={e => handleSelectEra(eras.find(er => er.id === e.target.value) || null)}
                  className="px-2.5 py-1.5 rounded-lg border text-xs outline-none"
                  style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                  <option value="">All Eras</option>
                  {eras.map(era => <option key={era.id} value={era.id}>{era.name}</option>)}
                </select>
              </div>
            </div>

            {activeTab === 'feed' && (
              <>
                <ComposeBox
                  eras={eras}
                  userId={user?.id || null}
                  userProfile={userProfile}
                  onPost={handlePost}
                  posting={posting}
                  postError={postError}
                  postSuccess={postSuccess}
                  onClearError={() => setPostError(null)}
                />

                <NewPostsPill count={newPostsCount} onDismiss={loadPosts} />

                {loadingPosts ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="rounded-xl border p-4 animate-pulse"
                        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
                        <div className="flex gap-3">
                          <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--muted)' }} />
                          <div className="flex-1 space-y-2">
                            <div className="h-3 rounded w-1/3" style={{ backgroundColor: 'var(--muted)' }} />
                            <div className="h-3 rounded w-full" style={{ backgroundColor: 'var(--muted)' }} />
                            <div className="h-3 rounded w-2/3" style={{ backgroundColor: 'var(--muted)' }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    {displayPosts.map(post => (
                      <PostCard
                        key={post.id}
                        post={post}
                        eraName={usingMockPosts ? undefined : eraMapRef.current[post.era_id]}
                        userId={user?.id || null}
                        onExpand={handleExpandPost}
                        expanded={expandedPost === post.id}
                        replies={replies[post.id] || []}
                        loadingReplies={loadingReplies === post.id}
                        onReply={handleReply}
                        isMock={usingMockPosts}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'amas' && (
              <AmaSection amas={amas} userId={user?.id || null} />
            )}

            {activeTab === 'challenges' && (
              <GapChallengesSection />
            )}

            {activeTab === 'trending' && (
              <TrendingFeedSection />
            )}
          </main>

          {/* ── Right Sidebar Column ── */}
          <RightSidebar eras={eras} amas={amas} userId={user?.id || null} />
        </div>
      </div>
    </div>
  );
}
